import Vue from "vue";
import VueRouter from "vue-router";
import {initializeAppVersion} from "@/utils/checkVersion";
import { normalizeSidebarLazyRoutes } from "./lazyRouteShell";
import routes from "./routes";
import { tokenExpired } from "./tokenExpired";
import i18n from "@/i18n";
import store from "@/store";
import { eventBus } from "@/main";
import EventCenter, { setPackageExpiredPending } from "@/utils/EventCenter";
import { setPageTitle } from "@/utils/documentTitle";

let token = localStorage.getItem("token");

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch((err) => err);
};

Vue.use(VueRouter);

// 侧边栏懒加载页面先套同步外壳，避免生产环境首次下载页面 chunk 时阻塞路由确认。
normalizeSidebarLazyRoutes(routes);

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

// 应用启动时执行一次版本检测
initializeAppVersion();

// 无权限时的统一落地页，避免使用 next(false) 让受限地址停留在空页面。
const NO_PERMISSION_REDIRECT = "/dashboard";

/**
 * 读取当前导航要求的角色（1 超管 2 管理员 3 普通成员）。
 * vue-router 3 的 to.meta 只取最后一条匹配记录的 meta，父路由上声明的 roles 不会合并进来，
 * 因此这里遍历 to.matched，取最靠近当前页面的那条声明（子路由可覆盖父路由）。
 * @param {import('vue-router').Route} to
 * @returns {number[]|null}
 */
const getRequiredRoles = (to) => {
  const records = (to.matched || []).filter((record) => Array.isArray(record.meta?.roles));
  const record = records[records.length - 1];
  if (!record || !record.meta.roles.length) return null;
  return record.meta.roles.map((role) => Number(role));
};

/**
 * 当前团队角色，未登录/未加载时返回 null。
 * 后端有可能返回字符串角色，统一转成数字避免 [1, 2].includes("2") 误判。
 */
const getCurrentRole = () => {
  const role = store.getters["authoState/current_team"]?.role;
  if (role === undefined || role === null || role === "") return null;
  const normalized = Number(role);
  return Number.isNaN(normalized) ? null : normalized;
};

let teamRolePromise = null; // 并发导航共用同一次补拉请求，避免重复调用团队列表接口

const fetchCurrentRole = async () => {
  // current_team 原本只在 Layout 挂载后由团队列表接口写入，刷新/直达受限页面时守卫可能先执行，
  // 这里补拉一次并回填 store，保证权限判断拿到真实角色。
  const { default: teamApi } = await import("@/api/teamFetch");
  const res = await teamApi.getUserTeamList();
  const list = res?.code === 1 ? res.data?.data || [] : [];
  const activeTeam = list.find((item) => item.is_use === 1) || list[0];
  if (activeTeam) {
    store.commit("authoState/CHANGE_TEAM_INFO", activeTeam);
  }
  return getCurrentRole();
};

const ensureCurrentRole = () => {
  const role = getCurrentRole();
  if (role !== null) return Promise.resolve(role);
  if (!teamRolePromise) {
    teamRolePromise = fetchCurrentRole()
      .catch((error) => {
        console.error("路由权限校验：获取当前团队角色失败", error);
        return null;
      })
      .then((roleValue) => {
        teamRolePromise = null;
        return roleValue;
      });
  }
  return teamRolePromise;
};

router.beforeEach(async (to, from, next) => {
  // console.log(to, from);
  const tokenTimestamp = localStorage.getItem("tokenTimestamp");
  const currentTime = new Date().getTime();
  const tokenExpiryTime = 24 * 60 * 60 * 1000; // 24 小时
  token = localStorage.getItem("token");

  if (to.matched.length === 0) {
    next("/404");
    return;
  }

  if (to?.meta?.crumbs) {
    // crumbs 已国际化为 i18n key（route.xxx），英文原样保留；统一交给标题工具，避免覆盖通知滚动。
    setPageTitle(to.meta.crumbs.startsWith("route.") ? i18n.t(to.meta.crumbs) : to.meta.crumbs);
  }

  // 舆情模块权限验证
  // if (to.path.startsWith("/sentiment") && to.path !== "/sentimentNoRecharge") {
  //   const user_info = store.getters["authoState/user_info"];

  //   // 检查sentiment_valid字段，如果为false则跳转到无权限页面
  //   if (user_info && user_info.sentiment_valid === false) {
  //     next("/sentimentNoRecharge");
  //     return;
  //   }
  // }

  if (to.meta.isLogin === false) {
    next();
  } else {
    if (token) {
      if (tokenTimestamp && currentTime - tokenTimestamp > tokenExpiryTime) {
        tokenExpired();
        next("/homepage");
      } else {
        localStorage.setItem(`tokenTimestamp`, new Date().getTime());
        // 套餐不可用（无套餐或已过期 remaining_days < 0）时拦截非白名单路由（个人中心 /team 除外），改道套餐页并弹窗提示。
        const isAllowedPath = to.path.startsWith("/personal") || to.path.startsWith("/team") ;
        const noValidPackage = store.getters["team/getPackageInvalid"];
        if (to.meta.isLogin !== false && noValidPackage && !isAllowedPath) {
          // 普通成员无法管理套餐，统一落到套餐过期页；管理员保留弹窗和套餐页入口。
          const role = await ensureCurrentRole();
          if (role === 3) {
            next("/team/package/expired");
            return;
          }
          // 记录「需要弹窗」标志：冷启动时根节点尚未挂载，事件会丢失，靠标志兜底；
          // 团队页/个人中心等白名单页刷新不设标志，不会误弹。
          setPackageExpiredPending(true);
          // 延迟 emit，确保根节点已挂载弹窗组件并注册监听后再弹窗。
          setTimeout(() => eventBus.$emit(EventCenter.openPackageExpiredDialog), 0);
          next("/team/package");
          return;
        }
        next();
      }
    } else {
      tokenExpired();
      next("/homepage");
    }
  }
});

// 第二个路由守卫：由 meta.roles 声明允许访问的角色，集中处理团队等受限路由。
router.beforeEach(async (to, from, next) => {
  const requiredRoles = getRequiredRoles(to);
  if (!requiredRoles) {
    next();
    return;
  }

  const role = await ensureCurrentRole();

  // 角色获取失败时放行，避免接口异常导致无限重定向。
  if (role === null || requiredRoles.includes(role)) {
    next();
    return;
  }

  // 这里不能用 next(false)：初始导航（刷新/直接访问受限地址）时 from 是未匹配的 "/"，
  // vue-router 会把地址栏还原成 "/" 且不渲染任何组件，表现为跳到了空地址的白屏。
  if (to.path === NO_PERMISSION_REDIRECT || to.path === "/") {
    next();
    return;
  }
  next(NO_PERMISSION_REDIRECT);
});

// // 监听store中user_info的变化，当sentiment_valid发生变化时，根据变化情况处理路由跳转
// store.watch(
//   (state, getters) => getters["authoState/user_info"]?.sentiment_valid,
//   (newVal, oldVal) => {
//     const currentPath = router.currentRoute.path;

//     // 当sentiment_valid从true变为false时，如果当前在舆情模块则跳转到无权限页面
//     if (oldVal === true && newVal === false) {
//       if (currentPath.startsWith("/sentiment") && currentPath !== "/sentimentNoRecharge") {
//         router.push("/sentimentNoRecharge");
//       }
//     }
//     // 当sentiment_valid从false变为true时，如果当前在无权限页面则跳转到舆情首页
//     else if (oldVal === false && newVal === true) {
//       if (currentPath === "/sentimentNoRecharge") {
//         router.push("/sentiment");
//       }
//     }
//   }
// );

export default router;
